import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Navbar from './components/layout/navbar';
import Modal from './components/ui/modal';
import Home from './pages/home';
import HowItWorks from './pages/how-it-works';
import Pricing from './pages/pricing';
import DashboardPage from './pages/dashboard';
import { ProtectedRoute } from './components/auth/protected-route';
import { authService } from './services/auth';

const AppContent: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [currentStage, setCurrentStage] = useState(1);
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const lastClickTimeRef = useRef<number>(Date.now());
  const location = useLocation();

  const totalStages = 3;
  const AUTO_ADVANCE_DELAY = 6000; // 6 seconds in milliseconds

  // Check authentication status and redirect logged-in users from homepage
  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      try {
        await authService.waitForInitialization();
        
        // Only redirect if we're on the homepage and user is authenticated
        // Don't redirect if there are any URL parameters (like loginRequired)
        if (location.pathname === '/' && authService.isAuthenticated() && location.search === '') {
          window.location.href = '/dashboard';
          return;
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
      } finally {
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndRedirect();
  }, [location.pathname, location.search]);

  // Check for loginRequired parameter and open modal
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get('loginRequired') === 'true') {
      setModalOpen(true);
      setShowLoginForm(true);
      setLoginError('Please log in to access that page.');
      // Clean up the URL parameter
      window.history.replaceState({}, '', '/');
    }
  }, [location]);

  // Auto-cycle through dashboard stages - 6 seconds unless user clicks
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastClick = now - lastClickTimeRef.current;
      
      // Auto-advance if 6 seconds have passed since last user interaction
      if (timeSinceLastClick >= AUTO_ADVANCE_DELAY) {
        setCurrentStage(prev => {
          if (prev === totalStages) {
            return 1; // Loop back to first slide
          }
          return prev + 1;
        });
        // Reset timer after auto-advancing
        lastClickTimeRef.current = now;
      }
    }, 1000); // Check every second

    return () => clearInterval(interval);
  }, []);

  // Handle escape key for modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setModalOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  const openModal = () => {
    setModalOpen(true);
    setLoginError(''); // Clear any existing error when manually opening modal
  };

  const closeModal = () => {
    setModalOpen(false);
    setShowLoginForm(false);
    setLoginError(''); // Clear error when closing modal
  };

  const nextStage = () => {
    // Record user interaction and advance to next stage
    lastClickTimeRef.current = Date.now();
    if (currentStage < totalStages) {
      setCurrentStage(currentStage + 1);
    } else {
      setCurrentStage(1); // Loop back to first slide
    }
  };

  const toggleLoginForm = () => {
    setShowLoginForm(!showLoginForm);
    setLoginError(''); // Clear error when switching forms
  };

  const handleAuthSuccess = () => {
    setModalOpen(false);
    setLoginError(''); // Clear error on successful auth
  };

  // Show loading while checking authentication
  if (isCheckingAuth && location.pathname === '/') {
    return (
      <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, var(--obsidian-black) 0%, #1a1d23 100%)' }} />
    );
  }

  return (
    <div className="app">
      <Routes>
        <Route path="/" element={
          <>
            <Navbar onOpenModal={openModal} />
            <Home 
              currentStage={currentStage}
              onNextStage={nextStage}
              onOpenModal={openModal}
            />
            <Modal 
              isOpen={modalOpen}
              onClose={closeModal}
              showLoginForm={showLoginForm}
              onToggleForm={toggleLoginForm}
              onAuthSuccess={handleAuthSuccess}
              errorMessage={loginError}
            />
            <footer className="footer">
              <div className="container">
                <div className="footer-content">
                  <p className="footer-copyright">&copy; 2025 Briqq. All rights reserved.</p>
                  <div className="footer-disclaimer">
                    <p><strong>Important Disclaimer:</strong> For informational purposes only and does not constitute personal financial advice. Consult a qualified financial advisor for investment decisions. Briqq does not provide investment recommendations or trading advice.</p>
                  </div>
                </div>
              </div>
            </footer>
          </>
        } />
        <Route path="/how-it-works" element={
          <>
            <Navbar onOpenModal={openModal} />
            <HowItWorks />
            <Modal 
              isOpen={modalOpen}
              onClose={closeModal}
              showLoginForm={showLoginForm}
              onToggleForm={toggleLoginForm}
              onAuthSuccess={handleAuthSuccess}
              errorMessage={loginError}
            />
            <footer className="footer">
              <div className="container">
                <div className="footer-content">
                  <p className="footer-copyright">&copy; 2025 Briqq. All rights reserved.</p>
                  <div className="footer-disclaimer">
                    <p><strong>Important Disclaimer:</strong> For informational purposes only and does not constitute personal financial advice. Consult a qualified financial advisor for investment decisions. Briqq does not provide investment recommendations or trading advice.</p>
                  </div>
                </div>
              </div>
            </footer>
          </>
        } />
        <Route path="/pricing" element={
          <>
            <Navbar onOpenModal={openModal} />
            <Pricing onOpenModal={openModal} />
            <Modal 
              isOpen={modalOpen}
              onClose={closeModal}
              showLoginForm={showLoginForm}
              onToggleForm={toggleLoginForm}
              onAuthSuccess={handleAuthSuccess}
              errorMessage={loginError}
            />
            <footer className="footer">
              <div className="container">
                <div className="footer-content">
                  <p className="footer-copyright">&copy; 2025 Briqq. All rights reserved.</p>
                  <div className="footer-disclaimer">
                    <p><strong>Important Disclaimer:</strong> For informational purposes only and does not constitute personal financial advice. Consult a qualified financial advisor for investment decisions. Briqq does not provide investment recommendations or trading advice.</p>
                  </div>
                </div>
              </div>
            </footer>
          </>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardPage />
            <footer className="footer">
              <div className="container">
                <div className="footer-content">
                  <p className="footer-copyright">&copy; 2025 Briqq. All rights reserved.</p>
                  <div className="footer-disclaimer">
                    <p><strong>Important Disclaimer:</strong> For informational purposes only and does not constitute personal financial advice. Consult a qualified financial advisor for investment decisions. Briqq does not provide investment recommendations or trading advice.</p>
                  </div>
                </div>
              </div>
            </footer>
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AppContent />
    </Router>
  );
};

export default App; 